import "./FormResponseViewPage.scss";

import { Badge, Col, Form, Modal, Row } from "react-bootstrap";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import AnamneseAPI from "../../api/AnamneseAPI";
import AppButton from "../../../../../commons/buttons/AppButton";
import AppLinkButton from "../../../../../commons/buttons/AppLinkButton";
import AppLoading from "../../../../../commons/AppLoading";
import AppTextAreaInput from "../../../../../commons/inputs/AppTextAreaInput";
import HallAppToast from "../../../../../commons/HallAppToast";
import ProtocolAPI from "../../api/ProtocolAPI";
import ReportHeader from "../../../../report-full/components/ReportHeader";
import TrichoAPI from "../../api/TrichoAPI";

const anamneseAPI = new AnamneseAPI();
const protocolAPI = new ProtocolAPI();
const trichoAPI = new TrichoAPI();

const HALLAPP_PURPLE = "#7d4ee0";

// helpers
const pad2 = (n) => String(n).padStart(2, "0");

const formatBrDateTime = (isoDt) => {
  if (!isoDt) return "";
  const d = new Date(isoDt);
  if (Number.isNaN(d.getTime())) return "";
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()} ${pad2(d.getHours())}:${pad2(
    d.getMinutes()
  )}`;
};

const formatBrDate = (iso) => {
  if (!iso) return "";
  const s = String(iso);
  if (s.includes("T")) return formatBrDateTime(s).split(" ")[0];
  const [y, m, d] = s.split("-");
  if (!y || !m || !d) return s;
  return `${pad2(d)}/${pad2(m)}/${y}`;
};

function normalizeAnswer(answer) {
  if (answer === null || answer === undefined) return null;

  if (typeof answer === "object" && answer !== null) {
    if (answer.valueJson !== undefined) return answer.valueJson;
    if (answer.value_json !== undefined) return answer.value_json;
  }

  if (typeof answer === "string") {
    const trimmed = answer.trim();
    if ((trimmed.startsWith("{") && trimmed.endsWith("}")) || (trimmed.startsWith("[") && trimmed.endsWith("]"))) {
      try {
        return JSON.parse(trimmed);
      } catch {
        return answer;
      }
    }
    return answer;
  }

  return answer;
}

function parseConfigJson(configJson) {
  if (!configJson) return null;
  if (typeof configJson === "object") return configJson;
  if (typeof configJson === "string") {
    try {
      return JSON.parse(configJson);
    } catch {
      return null;
    }
  }
  return null;
}

function renderAnswerText(fieldType, valueJson) {
  if (valueJson === null || valueJson === undefined) return "—";

  if (typeof valueJson === "string") {
    if (fieldType === "DATE") return formatBrDate(valueJson);
    return valueJson.trim() ? valueJson : "—";
  }
  if (typeof valueJson === "number") return Number.isFinite(valueJson) ? String(valueJson) : "—";
  if (typeof valueJson === "boolean") return valueJson ? "Sim" : "Não";
  if (Array.isArray(valueJson)) return valueJson.length ? valueJson.join(", ") : "—";

  const v = valueJson;

  if (v?.text !== undefined) return String(v.text || "").trim() || "—";
  if (v?.bool !== undefined) return v.bool ? "Sim" : "Não";
  if (v?.number !== undefined) return v.number === null || v.number === undefined ? "—" : String(v.number);
  if (v?.date !== undefined) return v.date ? formatBrDate(v.date) : "—";
  if (v?.values !== undefined) return Array.isArray(v.values) && v.values.length ? v.values.join(", ") : "—";

  if (v?.value !== undefined) {
    if (v.value === null || v.value === undefined) return "—";
    if (typeof v.value === "string" && fieldType === "DATE") return formatBrDate(v.value);
    return String(v.value).trim() || "—";
  }

  try {
    return JSON.stringify(v);
  } catch {
    return "—";
  }
}

function renderAnswerNode(fieldType, raw, cfg) {
  const upper = String(fieldType || "").toUpperCase();

  if (upper === "IMAGE_UPLOAD") {
    const url = raw?.url || raw?.value?.url || "";
    const name = raw?.originalName || raw?.original_name || "";

    if (!url) {
      const t = renderAnswerText(upper, raw);
      return <div className={`frv-field__value ${t === "—" ? "frv-field__value--empty" : ""}`}>{t}</div>;
    }

    return (
      <div className="frv-img">
        <div className="frv-img__frame">
          <img
            src={url}
            alt={name || "imagem"}
            className="frv-img__img"
            loading="lazy"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        </div>
        {name ? <div className="frv-img__meta">{name}</div> : null}
        <a className="frv-img__link" href={url} target="_blank" rel="noreferrer">
          Abrir imagem
        </a>
      </div>
    );
  }

  if (upper === "IMAGE_SELECT") {
    const optIndex = typeof raw?.optIndex === "number" ? raw.optIndex : null;
    const pickedText = raw?.text || "";

    const imageOptions = Array.isArray(cfg?.imageOptions) ? cfg.imageOptions : [];
    const selectedOpt = optIndex !== null ? imageOptions[optIndex] : null;

    const imgUrl = selectedOpt?.imageUrl || selectedOpt?.previewUrl || "";
    const label = selectedOpt?.text || pickedText || (optIndex !== null ? `Opção ${optIndex + 1}` : "—");

    if (!imgUrl && (optIndex === null || optIndex === undefined)) {
      return <div className="frv-field__value frv-field__value--empty">—</div>;
    }

    return (
      <div className="frv-img">
        {imgUrl ? (
          <div className="frv-img__frame">
            <img
              src={imgUrl}
              alt={label || "opção"}
              className="frv-img__img"
              loading="lazy"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          </div>
        ) : null}

        <div className={`frv-field__value ${label === "—" ? "frv-field__value--empty" : ""}`}>{label}</div>

        {imgUrl ? (
          <a className="frv-img__link" href={imgUrl} target="_blank" rel="noreferrer">
            Abrir imagem
          </a>
        ) : null}
      </div>
    );
  }

  const text = renderAnswerText(upper, raw);
  return <div className={`frv-field__value ${text === "—" ? "frv-field__value--empty" : ""}`}>{text}</div>;
}

export default function FormResponseViewPage() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [isLoading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const [isLoadingPDF, setLoadingPDF] = useState(false);
  const [isLoadingDiagnostic, setIsLoadingDiagnostic] = useState(false);

  // ✅ protocolos (catálogo + vinculados)
  const [protocols, setProtocols] = useState([]); // catálogo
  const [linkedProtocols, setLinkedProtocols] = useState([]); // vinculados
  const [selectedProtocolId, setSelectedProtocolId] = useState("");
  const [isLoadingProtocols, setIsLoadingProtocols] = useState(false);
  const [isSavingProtocolLink, setIsSavingProtocolLink] = useState(false);

  // ✅ modal conteúdo HTML (full modal)
  const [showProtocolModal, setShowProtocolModal] = useState(false);
  const [protocolModalTitle, setProtocolModalTitle] = useState("");
  const [protocolModalHtml, setProtocolModalHtml] = useState("");

  // ✅ tricoscopia
  const [trinoscopies, setTrinoscopies] = useState([]);
  const [isLoadingTrinoscopies, setIsLoadingTrinoscopies] = useState(false);

  // diagnóstico
  const [description, setDescription] = useState("");

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        const res = await anamneseAPI?.getById(id);
        const payload = res.data;

        setData(payload);

        // diagnóstico
        setDescription(payload?.description || payload?.diagnostic || "");
      } catch (e) {
        HallAppToast.error?.("Não foi possível carregar a ficha.") ?? HallAppToast.genericError?.();
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  async function fetchAllProtocols() {
    try {
      setIsLoadingProtocols(true);
      const resp = await protocolAPI.findAll();
      setProtocols(Array.isArray(resp?.data) ? resp.data : []);
    } catch (_) {
      setProtocols([]);
      HallAppToast.genericError();
    } finally {
      setIsLoadingProtocols(false);
    }
  }

  async function fetchAllTrinoscopies() {
    try {
      setIsLoadingTrinoscopies(true);
      const resp = await trichoAPI.getAllByAnamneseResponseId(id);
      setTrinoscopies(Array.isArray(resp?.data) ? resp.data : []);
    } catch (_) {
      setTrinoscopies([]);
      HallAppToast.genericError();
    } finally {
      setIsLoadingTrinoscopies(false);
    }
  }

  // ✅ carrega protocolos vinculados via GET /{answerId}/protocols (já vem documents/customHtml)
  async function fetchProtocolsAnamnese() {
    try {
      setIsLoadingProtocols(true);

      const resp = await anamneseAPI.getProtocols(id);
      const list = Array.isArray(resp?.data) ? resp.data : [];

      setLinkedProtocols(list);
    } catch (_) {
      setLinkedProtocols([]);
      HallAppToast.genericError();
    } finally {
      setIsLoadingProtocols(false);
    }
  }

  // carrega catálogo + vinculados + tricoscopias quando abrir a tela
  useEffect(() => {
    fetchAllProtocols();
    fetchProtocolsAnamnese();
    fetchAllTrinoscopies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const header = useMemo(() => {
    const status = data?.status || "";
    const isSubmitted = String(status).toUpperCase() === "SUBMITTED";
    return {
      status,
      isSubmitted,
      badgeBg: isSubmitted ? "success" : "warning",
      badgeLabel: isSubmitted ? "Enviada" : "Rascunho",
    };
  }, [data?.status]);

  const fields = useMemo(() => data?.template?.fields || [], [data]);

  const generatePDF = async () => {
    try {
      setLoadingPDF(true);
      const response = await anamneseAPI.generatePDF(id);

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      window.open(url, "_blank");
    } catch (_) {
      HallAppToast.error("Não foi possível gerar o PDF");
    } finally {
      setLoadingPDF(false);
    }
  };

  const saveDiagnostic = async () => {
    try {
      setIsLoadingDiagnostic(true);
      const payload = { description };
      await anamneseAPI.saveDiagnostic(id, payload);
      HallAppToast.success();
    } catch (_) {
      HallAppToast.error("Não foi possível salvar o diagnostico.");
    } finally {
      setIsLoadingDiagnostic(false);
    }
  };

  // ✅ abrir PDF do protocolo vinculado (nova aba)
  function openProtocolPdf(p, e) {
    e?.stopPropagation?.();
    const url = p?.documents?.[0]?.fileUrl;
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  }

  // ✅ abrir conteúdo HTML em modal full
  function openProtocolContent(p, e) {
    e?.stopPropagation?.();
    const html = p?.customHtml || "";
    if (!html.trim()) {
      HallAppToast.warning("Este protocolo não possui conteúdo.");
      return;
    }
    setProtocolModalTitle(p?.name || "Conteúdo do protocolo");
    setProtocolModalHtml(html);
    setShowProtocolModal(true);
  }

  function closeProtocolModal() {
    setShowProtocolModal(false);
    setProtocolModalTitle("");
    setProtocolModalHtml("");
  }

  // ✅ opções disponíveis: catálogo - (já vinculados)
  const availableProtocolOptions = useMemo(() => {
    const linkedIds = new Set((linkedProtocols || []).map((x) => x?.id).filter(Boolean));
    return (protocols || []).filter((p) => !linkedIds.has(p.id));
  }, [protocols, linkedProtocols]);

  async function addProtocolToResponse() {
    if (!selectedProtocolId) {
      HallAppToast.warning("Selecione um protocolo.");
      return;
    }

    const protocolIdNum = Number(selectedProtocolId);
    if (!protocolIdNum) {
      HallAppToast.warning("Seleção inválida.");
      return;
    }

    try {
      setIsSavingProtocolLink(true);

      await anamneseAPI.addProtocolToResponse(id, protocolIdNum);

      // recarrega
      await fetchProtocolsAnamnese();

      setSelectedProtocolId("");
      HallAppToast.successWithMessage?.("Protocolo adicionado.") ?? HallAppToast.success?.();
    } catch (_) {
      HallAppToast.error("Não foi possível adicionar o protocolo.");
    } finally {
      setIsSavingProtocolLink(false);
    }
  }

  async function removeProtocolFromResponse(protocolId, e) {
    e?.stopPropagation?.();

    try {
      setIsSavingProtocolLink(true);

      await anamneseAPI.removeProtocolFromResponse(id, protocolId);

      await fetchProtocolsAnamnese();

      HallAppToast.successWithMessage?.("Protocolo removido.") ?? HallAppToast.success?.();
    } catch (_) {
      HallAppToast.error("Não foi possível remover o protocolo.");
    } finally {
      setIsSavingProtocolLink(false);
    }
  }

  if (isLoading) {
    return (
      <div className="frv-page frv-center">
        <AppLoading />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="frv-page frv-center">
        <div className="frv-empty">
          <div className="frv-empty__title">Nada por aqui</div>
          <div className="frv-empty__text">Não encontramos essa ficha.</div>
          <div style={{ marginTop: 12 }}>
            <AppLinkButton name="Voltar" onClick={() => navigate(-1)} />
          </div>
        </div>
      </div>
    );
  }

  const templateName = data?.template?.name || "Ficha";
  const templateDesc = data?.template?.description || "";
  const filledDt = data?.filledDt ? formatBrDateTime(data.filledDt) : "";
  const created = data?.createDt ? formatBrDateTime(data.createDt) : "";
  const customerName = data?.customerName || "";

  return (
    <div className="frv-page">
      <ReportHeader name="Resposta Anamnese" />

      {/* ✅ MODAL FULL – conteúdo HTML do protocolo */}
      <Modal show={showProtocolModal} onHide={closeProtocolModal} size="xl" fullscreen centered={false} scrollable>
        <Modal.Header closeButton>
          <Modal.Title>{protocolModalTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="frv-proto-modal-content" dangerouslySetInnerHTML={{ __html: protocolModalHtml }} />
        </Modal.Body>
        <Modal.Footer>
          <AppButton principalColor={HALLAPP_PURPLE} name="Fechar" onClick={closeProtocolModal} />
        </Modal.Footer>
      </Modal>

      <div className="container frv-header frv-header--sticky">
        <div className="frv-header__top">
          <div className="frv-header__actions">
            <Badge bg={header.badgeBg} className="frv-badge">
              {header.badgeLabel}
            </Badge>
          </div>
        </div>

        <div className="frv-header__title">{templateName}</div>
        {templateDesc ? <div className="frv-header__subtitle">{templateDesc}</div> : null}

        {customerName ? (
          <div className="frv-customer">
            <span className="frv-customer__label">Cliente</span>
            <span className="frv-customer__value">{customerName}</span>
          </div>
        ) : null}

        <div className="frv-meta">
          {filledDt ? (
            <div className="frv-meta__item">
              <span className="frv-meta__label">Preenchida</span>
              <span className="frv-meta__value">{filledDt}</span>
            </div>
          ) : null}

          {created ? (
            <div className="frv-meta__item">
              <span className="frv-meta__label">Criada</span>
              <span className="frv-meta__value">{created}</span>
            </div>
          ) : null}
        </div>
      </div>

      <div style={{ marginTop: "10px" }} className="container frv-content">
        <Row className="g-3">
          <Col xs={12} md={8} lg={7}>
            <div className="frv-card">
              <div className="frv-card__title">Respostas</div>

              <div className="frv-fields">
                {fields.map((f) => {
                  const fieldType = String(f.fieldType || "").toUpperCase();
                  const cfg = parseConfigJson(f.configJson);
                  const raw = normalizeAnswer(f.answer);
                  const required = !!f.required;

                  const options = cfg?.options || [];

                  const selectedValues =
                    raw?.values && Array.isArray(raw.values)
                      ? raw.values
                      : fieldType === "CHECKBOX" && Array.isArray(raw)
                        ? raw
                        : null;

                  return (
                    <div key={f.id} className="frv-field">
                      <div className="frv-field__head">
                        <div className="frv-field__label">
                          {f.label}
                          {required ? <span className="frv-required">obrigatório</span> : null}
                        </div>

                        <div className="frv-chip">{fieldType}</div>
                      </div>

                      {renderAnswerNode(fieldType, raw, cfg)}

                      {(fieldType === "SELECT" || fieldType === "CHECKBOX") && options.length > 0 ? (
                        <div className="frv-options">
                          {options.map((op) => {
                            const isSelected =
                              fieldType === "CHECKBOX"
                                ? !!selectedValues?.includes(op)
                                : raw?.value === op || raw === op;

                            return (
                              <span key={op} className={`frv-option ${isSelected ? "frv-option--selected" : ""}`}>
                                {op}
                              </span>
                            );
                          })}
                        </div>
                      ) : null}

                      {f.helperText ? <div className="frv-field__helper">{f.helperText}</div> : null}
                    </div>
                  );
                })}

                {fields.length === 0 ? <div className="frv-empty-inline">Sem campos no template.</div> : null}
              </div>
            </div>
          </Col>

          <Col xs={12} md={4} lg={5}>
            <div className="frv-card">
              <div className="frv-card__title">Ações</div>

              <div className="frv-actions">
                <AppLinkButton name="Voltar" onClick={() => navigate(-1)} />
                {isLoadingPDF ? (
                  <AppLoading />
                ) : (
                  <AppButton principalColor={HALLAPP_PURPLE} name="Gerar PDF" onClick={generatePDF} fullWidth={true} />
                )}
              </div>

              <div className="frv-tip">Dica: no celular você pode adicionar essa tela na tela inicial e abrir como PWA.</div>
            </div>

            <div className="frv-card frv-card--soft">
              <div className="frv-card__title">Status</div>

              <div className="frv-status">
                <div className="frv-status__row">
                  <div className="frv-status__k">Situação</div>
                  <div className="frv-status__v">{header.badgeLabel}</div>
                </div>

                <div className="frv-status__row">
                  <div className="frv-status__k">Data</div>
                  <div className={`frv-status__v ${filledDt ? "" : "frv-muted"}`}>{filledDt || "—"}</div>
                </div>
              </div>
            </div>

            {/* ✅ CARD – PROTOCOLOS */}
            <div className="frv-card">
              <div className="frv-card__title">Protocolos</div>

              <div className="frv-proto">
                <div className="frv-proto__row">
                  <Form.Select
                    value={selectedProtocolId}
                    onChange={(e) => setSelectedProtocolId(e.target.value)}
                    disabled={isLoadingProtocols || isSavingProtocolLink}
                  >
                    <option value="">Selecione um protocolo...</option>
                    {availableProtocolOptions.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </Form.Select>

                  {isSavingProtocolLink ? (
                    <div className="frv-proto__loading">
                      <AppLoading />
                    </div>
                  ) : (
                    <AppButton principalColor={HALLAPP_PURPLE} name="Adicionar" onClick={addProtocolToResponse} fullWidth={true} />
                  )}
                </div>

                {linkedProtocols?.length ? (
                  <div className="frv-proto__list">
                    {linkedProtocols.map((p) => {
                      const mode = String(p?.mode || "").toUpperCase();
                      const hasPdf = mode === "PDF" && !!p?.documents?.[0]?.fileUrl;
                      const showContentBtn = mode !== "PDF" && !!String(p?.customHtml || "").trim();

                      return (
                        <div key={p.id} className="frv-proto__item">
                          <div className="frv-proto__info">
                            <div className="frv-proto__name">{p.name}</div>
                            {p.mode ? <div className="frv-proto__meta">{p.mode}</div> : null}
                          </div>

                          <div className="frv-proto__actions">
                            {hasPdf ? <AppLinkButton name="ver pdf" onClick={(e) => openProtocolPdf(p, e)} /> : null}
                            {showContentBtn ? (
                              <AppLinkButton name="ver conteúdo" onClick={(e) => openProtocolContent(p, e)} />
                            ) : null}

                            <AppLinkButton
                              name="remover"
                              onClick={(e) => removeProtocolFromResponse(p.id, e)}
                              className="frv-proto__remove"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="frv-proto__empty">Nenhum protocolo vinculado.</div>
                )}
              </div>
            </div>

            {/* ✅ CARD – Tricoscopia (lista igual protocolos) */}
            <div className="frv-card">
              <div className="frv-card__title">Tricoscopia</div>

              <div className="frv-proto">
                <div className="frv-proto__row">
                  <AppButton
                    principalColor={HALLAPP_PURPLE}
                    name="Nova analise"
                    onClick={() => navigate(`/analise-tricoscopica/anamnese/${id}`)}
                    fullWidth={true}
                  />
                </div>

                {isLoadingTrinoscopies ? (
                  <div className="frv-proto__loading">
                    <AppLoading />
                  </div>
                ) : trinoscopies?.length ? (
                  <div className="frv-proto__list">
                    {trinoscopies.map((t) => {
                      const title = String(t?.title || "").trim() || "Sem título";
                      const examDate = t?.examDate ? formatBrDate(t.examDate) : "—";

                      return (
                        <div key={t.id} className="frv-proto__item">
                          <div className="frv-proto__info">
                            <div className="frv-proto__name">{title}</div>
                            <div className={`frv-proto__meta ${examDate === "—" ? "frv-muted" : ""}`}>{examDate}</div>
                          </div>

                          <div className="frv-proto__actions">
                            <AppLinkButton
                              name="ver"
                              onClick={() => navigate(`/analise-tricoscopica/${t.id}`)}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="frv-proto__empty">Nenhuma análise cadastrada.</div>
                )}
              </div>
            </div>

            {/* 🔽 CARD – DIAGNÓSTICO */}
            <div className="frv-card">
              <div className="frv-card__title">Diagnóstico</div>

              <AppTextAreaInput
                errors={[]}
                touched={{}}
                name="description"
                onChange={(e) => setDescription(e.target.value)}
                value={description}
                type="text"
                label="Descrição do diagnóstico"
                rows={10}
              />

              {isLoadingDiagnostic ? (
                <AppLoading />
              ) : (
                <AppButton principalColor={HALLAPP_PURPLE} name="SALVAR" onClick={saveDiagnostic} fullWidth={true} />
              )}
            </div>
          </Col>
        </Row>

        <div className="frv-bottom-safe" />
      </div>
    </div>
  );
}